import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ModalController, NavParams } from '@ionic/angular';
import { MedicalInquiryService, MI_TAB } from '../../../services/medical-inquiry/medical-inquiry.service';
import { MedicalInquiryDataService } from '@omni/data-services/medical-inquiry/medical-inquiry.data.service';
import { MedicalInquiry, MedicalInquiryStatus } from '@omni/classes/medical-inquiry/medical-inquiry.class';
import { GlobalUtilityService } from '@omni/services/global-utility.service';
import { IndHeaderLeftDataModel } from '@omni/models/indHeaderLeftDataModel';
import { MainCardViewDataModel } from '@omni/models/MainCardViewDataModel';
import { DeviceService } from '@omni/services/device/device.service';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { IndFilterMenuComponent } from '@omni/components/shared/ind-filter-menu/ind-filter-menu';
import { OmniAccordionViewDataModel } from '@omni/models/omniAccordionViewDataModel';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'medical-inquiry-list',
  templateUrl: './medical-inquiry-list.component.html',
  styleUrls: ['./medical-inquiry-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedicalInquiryListComponent implements OnInit, OnDestroy {
  @Input() selectedTab: string;
  indHeaderLeftModel: IndHeaderLeftDataModel;
  searchText = '';
  filteredCount = 0;
  headerCountLabel = '';
  hasFilteredResults = false;
  displayedCards: MainCardViewDataModel[] = [];
  private _displayedMedicalInquiries: MedicalInquiry[] = [];
  /**
   * Guards the one-time side-menu auto-open of the first medical inquiry on
   * non-mobile (OMNI-60504). Once per component instance.
   */
  private _autoOpenedFirstMI = false;
  private _autoOpenFirstMITimer: ReturnType<typeof setTimeout> | null = null;
  /** MIs currently shown (subset for infinite scroll). Kept in sync with displayedCards for onCardClick. */
  public get displayedMedicalInquiries(): MedicalInquiry[] {
    return this._displayedMedicalInquiries;
  }
  public set displayedMedicalInquiries(value: MedicalInquiry[]) {
    this._displayedMedicalInquiries = value;
    this._maybeAutoOpenFirstMI();
  }
  filteredMedicalInquiries: MedicalInquiry[] = [];
  private readonly itemsPerPage = 20;
  private currentPage = 0;
  hasMoreItems = false;
  infiniteScrollDisabled = false;
  hasTeamViewFilter = false;
  noDataMessage = 'NO_MEDICAL_INQUIRIES';
  private searchSubscription: Subscription;
  private dataSubscription: Subscription;
  private selectedMISubscription: Subscription;
  private tabSubscription: Subscription;
  private offlineSubscription: Subscription;
  /** Timeout id for "reopen create form after discard" so we can cancel it when user navigates away (e.g. list item click). */
  private pendingReopenCreateTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public medicalInquiryService: MedicalInquiryService,
    public medicalInquiryDataService: MedicalInquiryDataService,
    public device: DeviceService,
    private readonly navParams: NavParams,
    private readonly dynamicFormsService: DynamicFormsService,
    private readonly translate: TranslateService,
    private readonly navService: NavigationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthenticationService,
    private readonly datePipe: DatePipe,
    private readonly utilityService: GlobalUtilityService,
    private readonly modalCtrl: ModalController
  ) {}

  /** Team tab: true while Dynamics fetch for team MIs is in progress. Drives list-area loading UI. */
  get showTeamLoading(): boolean {
    return (
      this.medicalInquiryService.selectedTab === MI_TAB.TEAM &&
      this.medicalInquiryService.teamInquiriesLoading
    );
  }

  ngOnInit() {
    this.hasTeamViewFilter = this.authService.hasFeatureAction(FeatureActionsMap.TEAMVIEW_FILTER);
    this.initHeaderLeft();
    this.searchSubscription = this.medicalInquiryService.searchEventSubject
      .pipe(debounceTime(200))
      .subscribe((text: string) => {
        this.searchText = text || '';
        this.refreshCards();
      });
    this.dataSubscription = this.medicalInquiryDataService.medicalInquiries$.subscribe(() => {
      if (this.medicalInquiryService.selectedTab !== MI_TAB.TEAM) {
        this.refreshCards();
      }
    });
    this.selectedMISubscription = this.medicalInquiryService.selectedMI$.subscribe(() => {
      this.updateSelectionState();
    });
    this.tabSubscription = this.medicalInquiryService.selectedTab$.subscribe(async (tab) => {
      this.medicalInquiryService.resetFilters();
      if (tab === MI_TAB.TEAM) {
        this.noDataMessage = 'NO_TEAM_INQUIRY';
        if (this.device.isOffline) {
          this.noDataMessage = 'TEAM_INQUIRY_ONLINE_ONLY';
          this.refreshCards();
          return;
        }
        await this.loadTeamMedicalInquiries();
      } else {
        this.noDataMessage = 'NO_MEDICAL_INQUIRIES';
        this.refreshCards();
      }
    });
    this.offlineSubscription = this.device.isOfflineObservable.subscribe(() => {
      if (this.medicalInquiryService.selectedTab === MI_TAB.TEAM) {
        this.noDataMessage = this.device.isOffline ? 'TEAM_INQUIRY_ONLINE_ONLY' : 'NO_TEAM_INQUIRY';
        this.cdr.markForCheck();
      }
    });
    this.loadMedicalInquiries();
  }

  ngOnDestroy() {
    if (this._autoOpenFirstMITimer) clearTimeout(this._autoOpenFirstMITimer);
    this.clearPendingReopenCreate();
    this.searchSubscription?.unsubscribe();
    this.dataSubscription?.unsubscribe();
    this.selectedMISubscription?.unsubscribe();
    this.tabSubscription?.unsubscribe();
    this.offlineSubscription?.unsubscribe();
    this.medicalInquiryService.resetTeamState();
  }

  private clearPendingReopenCreate(): void {
    if (this.pendingReopenCreateTimeoutId != null) {
      clearTimeout(this.pendingReopenCreateTimeoutId);
      this.pendingReopenCreateTimeoutId = null;
    }
  }

  private async loadMedicalInquiries() {
    if (this.device.isOffline) {
      void this.dynamicFormsService.syncOptionSetsForMedicalInquiry(true);
      await this.medicalInquiryDataService.fetchMedicalInquiries(true);
    } else {
      await this.medicalInquiryDataService.fetchMedicalInquiries(false);
    }
    this.refreshCards();
  }

  private async loadTeamMedicalInquiries(): Promise<void> {
    const childUsers = this.authService.user?.childUsers ?? [];
    if (childUsers.length === 0) {
      this.refreshCards();
      return;
    }

    this.medicalInquiryService.teamInquiriesLoading = true;
    this.cdr.markForCheck();

    try {
      const childUserIds = childUsers.map(u => u.userId);
      this.medicalInquiryService.teamMedicalInquiries =
        await this.medicalInquiryDataService.fetchTeamMedicalInquiries(childUserIds);
      await this.medicalInquiryDataService.fetchTeamMiProducts(
        this.medicalInquiryService.teamMedicalInquiries,
        childUserIds
      );
    } finally {
      this.medicalInquiryService.teamInquiriesLoading = false;
      this.refreshCards();
    }
  }

  private refreshCards() {
    const activeTab = this.medicalInquiryService.selectedTab;
    const currentUserId = this.authService.user?.systemUserID;

    const tabRawList = activeTab === MI_TAB.TEAM
      ? this.medicalInquiryService.teamMedicalInquiries
      : this.medicalInquiryDataService.medicalInquiries.filter(mi => mi.ownerId === currentUserId);

    const tabFilteredList = this.medicalInquiryService.applyFilters(tabRawList);

    const searchLower = this.searchText.toLowerCase();
    this.filteredMedicalInquiries = searchLower
      ? tabFilteredList.filter(mi => {
          const statusForSearch = this.getMedicalInquiryStatusLine(mi).toLowerCase();
          return (
            (mi.title || '').toLowerCase().includes(searchLower) ||
            (mi.id || '').toLowerCase().includes(searchLower) ||
            (mi.contactFormatted || '').toLowerCase().includes(searchLower) ||
            statusForSearch.includes(searchLower)
          );
        })
      : [...tabFilteredList];

    this.filteredMedicalInquiries.sort((a, b) => {
      const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
      const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
      return dateB - dateA;
    });

    this.filteredCount = this.filteredMedicalInquiries.length;
    this.hasFilteredResults = this.filteredCount > 0;
    this.infiniteScrollDisabled = false;

    const tabLabel = activeTab === MI_TAB.TEAM
      ? this.translate.instant('TEAM_MEDICAL_INQUIRIES')
      : this.translate.instant('MY_MEDICAL_INQUIRIES');
    this.headerCountLabel = searchLower
      ? this.translate.instant('AL_RESULTS') + ` (${this.filteredCount})`
      : `${tabLabel} (${this.filteredCount})`;

    this.currentPage = 0;
    this.loadMoreItems();
    this.initHeaderLeft();
    this.cdr.markForCheck();
  }

  private loadMoreItems(): void {
    const startIndex = this.currentPage * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const newItems = this.filteredMedicalInquiries.slice(startIndex, endIndex);

    if (this.currentPage === 0) {
      this.displayedMedicalInquiries = newItems;
      this.displayedCards = newItems.map(mi => this.toMainCardData(mi));
    } else {
      this.displayedMedicalInquiries = [...this.displayedMedicalInquiries, ...newItems];
      this.displayedCards = this.displayedMedicalInquiries.map(mi => this.toMainCardData(mi));
    }

    this.hasMoreItems = endIndex < this.filteredMedicalInquiries.length;
    this.currentPage++;
  }

  doInfinite(event: { target: { complete: () => void; disabled?: boolean } }): void {
    if (this.hasMoreItems) {
      this.loadMoreItems();
    }
    event.target.complete();
    if (!this.hasMoreItems) {
      this.infiniteScrollDisabled = true;
    }
  }

  /** Updates only isSelected on displayedCards when selection changes (avoids method in template). */
  private updateSelectionState(): void {
    const selectedId = this.medicalInquiryService.selectedMI?.id;
    for (const card of this.displayedCards) {
      card.isSelected = card.id === selectedId;
    }
    this.cdr.markForCheck();
  }

  toMainCardData(mi: MedicalInquiry): MainCardViewDataModel {
    const dateSource = mi.reportedDate || mi.createdOn;
    const primaryTextLeft = dateSource
      ? `${this.datePipe.transform(dateSource, 'MMM', undefined, this.translate.currentLang)}  ${dateSource.getDate()}`
      : '';
    const statusLine = this.getMedicalInquiryStatusLine(mi);
    const contactLine = this.getMedicalInquiryContactLine(mi);
    return {
      id: mi.id,
      fixedHeight: true,
      isExpandable: false,
      showArrow: false,
      primaryTextLeft,
      primaryTextRight: mi.displayTitle || this.translate.instant('MEDICAL_INQUIRY'),
      secondaryTextRight: statusLine || contactLine,
      secondaryTextRightTwo: statusLine ? contactLine : '',
      isSelected: this.medicalInquiryService.selectedMI?.id === mi.id
    };
  }

  /** Same two-line rule as Activities pane MI card (`activities-pane.ts` CaseIntake-style secondary). */
  private getMedicalInquiryStatusLine(mi: MedicalInquiry): string {
    const formatted = (mi.statusCodeFormatted || '').trim();
    if (formatted) {
      return formatted;
    }
    return this.getStatusFallbackLabel(mi.statusCode);
  }

  private getStatusFallbackLabel(statusCode: number): string {
    switch (statusCode) {
      case MedicalInquiryStatus.DRAFT:
        return this.translate.instant('OM_DRAFT');
      case MedicalInquiryStatus.SUBMITTED:
        return this.translate.instant('SUBMITTED');
      case MedicalInquiryStatus.INACTIVE:
        return this.translate.instant('INACTIVE');
      case MedicalInquiryStatus.COMPLETED:
        return this.translate.instant('COMPLETED');
      default:
        return '';
    }
  }

  /** Mirrors Customer Inquiry tool list empty-contact copy (`case-list-item`). */
  private getMedicalInquiryContactLine(mi: MedicalInquiry): string {
    return (
      (mi.contactFormatted || '').trim() ||
      `${this.translate.instant('NO')} ${this.utilityService.globalCustomerText}`
    );
  }

  private initHeaderLeft() {
    const isTeamTab = this.medicalInquiryService.selectedTab === MI_TAB.TEAM;
    const buttons: any[] = [
      {
        id: 'close',
        imgSrc: 'assets/imgs/back_to_home_btn.svg',
        isDisabled: false,
        align: 'left'
      },
    ];
    if (!isTeamTab) {
      buttons.push({
        id: 'plusNew',
        imgSrc: 'assets/imgs/ios_add_3x.svg',
        isDisabled: false,
        align: 'right',
        tooltip: this.translate.instant('CREATE_MEDICAL_INQUIRY')
      });
    }
    this.indHeaderLeftModel = {
      id: 'medical-inquiry-list-header-left',
      cssClass: 'main-tool-header-title',
      title: this.translate.instant('ALL_MEDICAL_INQUIRIES'),
      mode: true,
      controls: buttons
    };
  }

  get hideFilterButton(): boolean {
    return false;
  }

  get filterButtonBadgeCount(): number {
    return this.medicalInquiryService.selectedFilterData.length;
  }

  async onFilterClick(): Promise<void> {
    const categories = this.buildFilterCategories();

    const modal = await this.modalCtrl.create({
      component: IndFilterMenuComponent,
      cssClass: 'filter-menu-right-pane',
      backdropDismiss: false,
      componentProps: {
        viewData: {
          title: this.translate.instant('FILTERS'),
          data: categories,
        },
        selectedFilter: [...this.medicalInquiryService.selectedFilterData],
        from: PageName.MedicalInquiryHomePage,
      },
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();

    if (data?.isDone) {
      this.medicalInquiryService.selectedFilterData = data.selectedItems ?? [];
      this.refreshCards();
    }
  }

  private buildFilterCategories(): OmniAccordionViewDataModel[] {
    const isTeamTab = this.medicalInquiryService.selectedTab === MI_TAB.TEAM;
    const currentUserId = this.authService.user?.systemUserID;

    const sourceList = isTeamTab
      ? this.medicalInquiryService.teamMedicalInquiries
      : this.medicalInquiryDataService.medicalInquiries.filter(mi => mi.ownerId === currentUserId);

    const categories: OmniAccordionViewDataModel[] = [];

    const caseTypes = [...new Set(sourceList.map(mi => mi.caseTypeFormatted).filter(Boolean))];
    if (caseTypes.length > 0) {
      categories.push({
        displayType: 'Accordion',
        id: 'caseType',
        primaryText: this.translate.instant('CASE_TYPE'),
        isExpanded: false,
        showExpandIcon: true,
        showCollapseIcon: true,
        isSearchable: false,
        isSelectable: true,
        isAggregatedSection: false,
        childItems: caseTypes.sort().map(ct => ({
          id: `caseType_${ct}`,
          label: ct,
          value: ct,
          isChecked: false,
          type: 'general',
        })),
      });
    }

    const contacts = new Map<string, string>();
    for (const mi of sourceList) {
      if (mi.contactId && mi.contactFormatted) {
        contacts.set(mi.contactId, mi.contactFormatted);
      }
    }
    if (contacts.size > 0) {
      categories.push({
        displayType: 'Accordion',
        id: 'contact',
        primaryText: this.translate.instant('CONTACT'),
        isExpanded: false,
        showExpandIcon: true,
        showCollapseIcon: true,
        isSearchable: false,
        isSelectable: true,
        isAggregatedSection: false,
        childItems: [...contacts.entries()]
          .sort((a, b) => a[1].localeCompare(b[1]))
          .map(([id, name]) => ({
            id: `contact_${id}`,
            label: name,
            value: id,
            isChecked: false,
            type: 'general',
          })),
      });
    }

    const statuses = [...new Set(sourceList.map(mi => mi.statusCodeFormatted).filter(Boolean))];
    if (statuses.length > 0) {
      categories.push({
        displayType: 'Accordion',
        id: 'status',
        primaryText: this.translate.instant('STATUS'),
        isExpanded: false,
        showExpandIcon: true,
        showCollapseIcon: true,
        isSearchable: false,
        isSelectable: true,
        isAggregatedSection: false,
        childItems: statuses.sort().map(s => ({
          id: `status_${s}`,
          label: s,
          value: s,
          isChecked: false,
          type: 'general',
        })),
      });
    }

    if (isTeamTab && this.hasTeamViewFilter) {
      const childUsers = this.authService.user?.childUsers ?? [];
      if (childUsers.length > 0) {
        categories.push({
          displayType: 'Accordion',
          id: 'users',
          primaryText: this.translate.instant('USERS'),
          isExpanded: false,
          showExpandIcon: true,
          showCollapseIcon: true,
          isSearchable: false,
          isSelectable: true,
          isAggregatedSection: false,
          childItems: childUsers
            .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''))
            .map(u => ({
              id: `user_${u.userId}`,
              label: u.fullName ?? u.userId,
              value: u.userId,
              isChecked: false,
              type: 'general',
            })),
        });
      }
    }

    const productMap = new Map<string, string>();
    for (const mi of sourceList) {
      for (const p of (mi.products ?? [])) {
        if (p.id && p.name && !productMap.has(p.id)) {
          productMap.set(p.id, p.name);
        }
      }
    }
    if (productMap.size > 0) {
      categories.push({
        displayType: 'Accordion',
        id: 'product',
        primaryText: this.translate.instant('PRODUCTS'),
        isExpanded: false,
        showExpandIcon: true,
        showCollapseIcon: true,
        isSearchable: productMap.size > 10,
        searchActiveAt: 10,
        isSelectable: true,
        isAggregatedSection: false,
        childItems: [...productMap.entries()]
          .sort((a, b) => a[1].localeCompare(b[1]))
          .map(([id, name]) => ({
            id: `product_${id}`,
            label: name,
            value: id,
            isChecked: false,
            type: 'general',
          })),
      });
    }

    return categories;
  }

  searchInput(event: Event): void {
    const val = (event?.target as HTMLInputElement | null)?.value ?? '';
    this.medicalInquiryService.searchText = val;
    this.medicalInquiryService.searchEventSubject.next(val);
  }

  async onControlClick(id: string) {
    if (id === 'close') {
      if (!(await this.medicalInquiryService.confirmDiscardIfDirty())) return;
      this.clearPendingReopenCreate();
      this.navService.popWithPageTracking();
    } else if (id === 'plusNew') {
      if (!(await this.medicalInquiryService.confirmDiscardIfDirty())) return;
      this.clearPendingReopenCreate();
      this.medicalInquiryService.setSelectedMI(null);
      const wasShowingNew = this.medicalInquiryService.showNew;
      if (wasShowingNew) {
        this.medicalInquiryService.setShowNew(false);
        this.pendingReopenCreateTimeoutId = setTimeout(() => {
          this.pendingReopenCreateTimeoutId = null;
          this.medicalInquiryService.setShowNew(true);
        }, 0);
      } else {
        this.medicalInquiryService.setShowNew(true);
      }
    }
  }

  /**
   * Opens the first medical inquiry in the right pane once, when the tool is
   * opened via the side menu on non-mobile (OMNI-60504). Mirrors the
   * contact/account pattern: side-menu gate (__source), debounce so the final
   * sorted first item is opened, and the real open method (onCardClick). Never
   * overrides an existing selection.
   */
  private _maybeAutoOpenFirstMI(): void {
    if (this._autoOpenedFirstMI) return;
    if (this.navParams?.get('__source') !== 'menu') return;
    if (!this.device.isSplitViewActive) return;
    if (this.medicalInquiryService.selectedMI) return;
    if (!this._displayedMedicalInquiries || this._displayedMedicalInquiries.length === 0) return;
    if (this._autoOpenFirstMITimer) clearTimeout(this._autoOpenFirstMITimer);
    this._autoOpenFirstMITimer = setTimeout(() => {
      this._autoOpenFirstMITimer = null;
      if (this._autoOpenedFirstMI || this.medicalInquiryService.selectedMI) return;
      const firstMI = this._displayedMedicalInquiries && this._displayedMedicalInquiries[0];
      if (!firstMI) return;
      this._autoOpenedFirstMI = true;
      this.onCardClick(firstMI);
    }, 500);
  }

  async onCardClick(mi: MedicalInquiry) {
    if (!(await this.medicalInquiryService.confirmDiscardIfDirty())) return;
    this.clearPendingReopenCreate();
    this.medicalInquiryService.setSelectedMI(mi);
    this.medicalInquiryService.setShowNew(false);
  }

  trackByCardId(_index: number, card: MainCardViewDataModel): string {
    return card?.id || '';
  }

}
